import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { Button, Chip, Icon, Link } from '@material-ui/core';
import { format } from 'date-fns';
// Components
import App from '../../../components/app';
import ButtonTo from '../../../components/buttonTo';
import ErrorMessage from '../../../components/error-message';
import Listing from '../../../components/listing';
import { TooltipButton } from '../../../components/tooltip-button';
// Modules
import { actions, selectors } from '../../../redux/reducers/driver-communication';
import { selectors as errorSelectors } from '../../../redux/reducers/errors';
import { selectors as pendingSelectors } from '../../../redux/reducers/pendingOperations/module';
import { DRIVER_COMMUNICATION_STATUS, DriverCommunication } from '../../../redux/reducers/api-types';
// Ressources
import withGlobalStyles from '../../../utils/global-styles';
import { clsx } from '../../../utils/htmlUtils';
import MapUtils from '../../../utils/mapUtils';
import UserUtils from '../../../utils/userUtils';
import { createPath, paths } from '../../paths';
import useStyles from './driver-communications-list-style';

const EMPTY_VALUE = '—';

const formatDate = (iso: string): string => (iso ? format(new Date(iso), 'dd/MM/yyyy HH:mm') : EMPTY_VALUE);

interface DriverCommunicationsListPageProps {
    globalClasses: any;
}

const DriverCommunicationsListPage = ({ globalClasses }: DriverCommunicationsListPageProps): JSX.Element => {
    const { t } = useTranslation(['driver_communication', 'common']);
    const dispatch = useDispatch();
    const classes = useStyles();

    const communications = useSelector(selectors.communications);
    const loading = useSelector(pendingSelectors.isLoadingDriverCommunications);
    const error = useSelector(errorSelectors.getDriverCommunicationError);

    const [showEnded, setShowEnded] = React.useState<boolean>(false);

    useEffect(() => {
        dispatch(actions.getDriverCommunications());
    }, [dispatch]);

    // Chronological: the oldest start first, the furthest in the future last
    const sorted = React.useMemo(
        () =>
            [...communications].sort(
                (a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime() || a.id - b.id,
            ),
        [communications],
    );
    const endedCount = sorted.filter((c) => c.status === DRIVER_COMMUNICATION_STATUS.ENDED).length;
    const visible = showEnded ? sorted : sorted.filter((c) => c.status !== DRIVER_COMMUNICATION_STATUS.ENDED);

    const statusClass = (status: DRIVER_COMMUNICATION_STATUS): string => {
        if (status === DRIVER_COMMUNICATION_STATUS.ACTIVE) {
            return classes.chipActive;
        }
        return status === DRIVER_COMMUNICATION_STATUS.UPCOMING ? classes.chipUpcoming : classes.chipEnded;
    };

    //=======================================//
    //=============== Renders ===============//
    //=======================================//

    const renderDetail = (label: string, value: string, href?: string): JSX.Element => (
        <div className={classes.detail}>
            <div className={classes.detailLabel}>{label}</div>
            <div className={clsx(classes.detailValue, value === EMPTY_VALUE && classes.detailEmpty)}>
                {href ? (
                    <Link
                        underline="always"
                        className={classes.addressLink}
                        href={href}
                        target="_blank"
                        rel="noreferrer noopener"
                        title={t('fields.location_open')}
                    >
                        {value}
                    </Link>
                ) : (
                    value
                )}
            </div>
        </div>
    );

    const renderCommunication = (communication: DriverCommunication): JSX.Element => (
        <div key={communication.id} className={classes.communication}>
            <div className={classes.header}>
                <b className={classes.title}>{communication.title}</b>
                <Chip
                    size="small"
                    label={t(`status.${communication.status}`)}
                    className={clsx(classes.chip, statusClass(communication.status))}
                />
                <span style={{ flex: 1 }} />
                <TooltipButton title={t('edit_button')}>
                    <Button
                        size="small"
                        variant="contained"
                        color="secondary"
                        component={RouterLink}
                        to={createPath(paths.driverCommunicationsEdit, { id: communication.id })}
                        className={globalClasses.btnAutoWidth}
                    >
                        <Icon fontSize="small">edit</Icon>
                    </Button>
                </TooltipButton>
            </div>
            <div className={classes.description}>{communication.description}</div>
            <div className={classes.details}>
                {renderDetail(
                    t('fields.location'),
                    communication.location?.address || EMPTY_VALUE,
                    communication.location
                        ? MapUtils.googleMapsUrl(communication.location.lat, communication.location.lng)
                        : undefined,
                )}
                {renderDetail(t('fields.start_date'), formatDate(communication.start_date))}
                {renderDetail(t('fields.end_date'), formatDate(communication.end_date))}
            </div>
        </div>
    );

    return (
        <App
            hasRole={UserUtils.hasRoleOpsOrGod()}
            loading={loading}
            header={{
                title: t('title'),
                button: (
                    <ButtonTo
                        to={paths.driverCommunicationsAdd}
                        startIcon={<Icon>add</Icon>}
                        text={t('common:add')}
                        variante="rectangle"
                    />
                ),
            }}
        >
            <ErrorMessage payload={error} />
            <div className={globalClasses.block} style={{ padding: '20px 28px' }}>
                {endedCount > 0 && (
                    <div className={classes.toolbar}>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setShowEnded((shown) => !shown)}
                            startIcon={<Icon fontSize="small">{showEnded ? 'visibility_off' : 'visibility'}</Icon>}
                        >
                            {showEnded ? t('hide_ended') : t('show_ended', { nb: endedCount })}
                        </Button>
                    </div>
                )}
                <Listing
                    datas={visible.map(renderCommunication)}
                    customEmpty={<div style={{ textAlign: 'center', padding: 16 }}>{t('empty')}</div>}
                />
            </div>
        </App>
    );
};

export default withGlobalStyles(DriverCommunicationsListPage);
